import React, { useId } from 'react';
import { normalizeCategory } from './categories';
import { useMaskBalance } from './balanceVisibility';

// Короткі назви місяців (укр.), індекс = getMonth()
const UA_MONTHS_SHORT = ['січ', 'лют', 'бер', 'кві', 'тра', 'чер', 'лип', 'сер', 'вер', 'жов', 'лис', 'гру'];

// Витрати за категорією по 6 місяцях: поточний місяць — останній,
// перед ним 5 попередніх (пів року). Рахуємо тільки витрати (type='expense')
// цієї ж категорії. Повертає { months: [{label, value}], total }.
export function getCategoryMonthly(transactions, category) {
  const now = new Date();
  const buckets = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    buckets.push({ year: d.getFullYear(), month: d.getMonth(), label: UA_MONTHS_SHORT[d.getMonth()], value: 0 });
  }
  const index = {};
  buckets.forEach((b, i) => { index[`${b.year}-${b.month}`] = i; });

  (transactions || []).forEach((tx) => {
    if (!tx || normalizeCategory(tx.category) !== normalizeCategory(category) || tx.type !== 'expense') return;
    const d = new Date(tx.date);
    if (isNaN(d.getTime())) return;
    const idx = index[`${d.getFullYear()}-${d.getMonth()}`];
    if (idx === undefined) return;
    buckets[idx].value += Math.abs(parseFloat(tx.amount) || 0);
  });

  const total = buckets.reduce((s, b) => s + b.value, 0);
  return { months: buckets, total };
}

// Формат числа з пробілами між тисячами: 2825 → "2 825"
export function fmtAmount(n) {
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

// Прямокутник зі скругленими лише верхніми кутами, «прибитий» до базової лінії
function barPath(x, y, w, h, r) {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  return `M${x},${y + h} L${x},${y + rr} Q${x},${y} ${x + rr},${y} L${x + w - rr},${y} Q${x + w},${y} ${x + w},${y + rr} L${x + w},${y + h} Z`;
}

// Підпис осі: цілі — «900 ₴», дробові — «900.62 ₴». Копійки відкидаються,
// а не округлюються, як у monobank: 35 622.99 / 2 → «17 811.49»
function fmtAxis(n) {
  const v = Math.floor(n * 100 + 1e-6) / 100;
  const [i, d] = (Number.isInteger(v) ? String(v) : v.toFixed(2)).split('.');
  return i.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + (d ? `.${d}` : '');
}

// Ширина підпису осі (шрифт SF 12.5px) — колонка підписів підлаштовується
// під найдовший: «900.62 ₴» вузька, «35 622.99 ₴» — ширша
let measureCtx = null;
function labelWidth(text) {
  try {
    measureCtx = measureCtx || document.createElement('canvas').getContext('2d');
    measureCtx.font = '400 12.5px SF, sans-serif';
    return measureCtx.measureText(text).width;
  } catch (e) {
    return text.length * 7;
  }
}

// Стовпчикова діаграма витрат за категорією по місяцях. Геометрія знята зі
// скріншотів monobank (картка 358×143): підписи осі — від 16.7px зліва,
// вирівняні праворуч по найдовшому; сітка — на 9.3px правіше і до правого
// краю; 6 стовпчиків по 16px з кроком (ширина сітки / 6.67), перший — на
// крок від початку сітки. colors — градієнт стовпчиків [верх, низ] категорії
// (src/categories.js).
const CategoryMonthlyChart = ({ months, colors }) => {
  const gradId = useId().replace(/:/g, '');
  const maskBalance = useMaskBalance();
  const W = 358;
  const H = 143;
  const LABEL_L = 16.7;
  const TOP = 28.2;
  const BASE = 110.4;
  const plotH = BASE - TOP;
  const BAR_W = 16;
  const MONTH_Y = 131.4;

  const maxVal = Math.max(0, ...months.map((m) => m.value));
  const scaleMax = maxVal > 0 ? maxVal : 1;
  const labels = [maskBalance(`${fmtAxis(maxVal)} ₴`), maskBalance(`${fmtAxis(maxVal / 2)} ₴`), '0 ₴'];
  const LABEL_R = LABEL_L + Math.max(...labels.map(labelWidth));
  const GRID_L = LABEL_R + 9.3;
  const STEP = (W - GRID_L) / 6.67;
  const FIRST_BAR = GRID_L + STEP;
  const [top, bottom] = colors || ['#436469', '#5ec287'];

  const GRID = '#404040';
  const AXIS = '#5b5b5b';
  const LABEL = '#a8a8a8';
  const YINK = '#ffffff';
  const ZERO = '#deddde';
  const font = { fontSize: 12.5, fontFamily: 'SF, sans-serif' };

  return (
    <svg
      className="cat-chart"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Витрати за категорією по місяцях"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={top} />
          <stop offset="100%" stopColor={bottom} />
        </linearGradient>
      </defs>

      {/* Сітка: максимум (верх), половина, нуль (базова лінія) */}
      <line x1={GRID_L} y1={TOP} x2={W} y2={TOP} stroke={GRID} strokeWidth="1" strokeDasharray="4 4" />
      <line x1={GRID_L} y1={TOP + plotH / 2} x2={W} y2={TOP + plotH / 2} stroke={GRID} strokeWidth="1" strokeDasharray="4 4" />
      <line x1={GRID_L} y1={BASE} x2={W} y2={BASE} stroke={AXIS} strokeWidth="1" />

      {/* Підписи осі Y — над своїми лініями */}
      <text x={LABEL_R} y={TOP - 1.2} textAnchor="end" fill={YINK} {...font}>{labels[0]}</text>
      <text x={LABEL_R} y={TOP + plotH / 2 - 1.2} textAnchor="end" fill={YINK} {...font}>{labels[1]}</text>
      <text x={LABEL_R} y={BASE - 1.2} textAnchor="end" fill={YINK} {...font}>{labels[2]}</text>

      {/* Стовпчики */}
      {months.map((m, i) => {
        const x = FIRST_BAR + STEP * i - BAR_W / 2;
        if (m.value <= 0) {
          return <path key={`b${i}`} d={barPath(x, BASE - 3.5, BAR_W, 3.5, 1.75)} fill={ZERO} />;
        }
        const h = Math.max(6, (m.value / scaleMax) * plotH);
        return <path key={`b${i}`} d={barPath(x, BASE - h, BAR_W, h, 3)} fill={`url(#${gradId})`} />;
      })}

      {/* Підписи місяців */}
      {months.map((m, i) => (
        <text key={`l${i}`} x={FIRST_BAR + STEP * i} y={MONTH_Y} textAnchor="middle" fill={LABEL} {...font}>{m.label}</text>
      ))}
    </svg>
  );
};

export default CategoryMonthlyChart;
